import { EMPTY_MARK, formatWon } from "@buildpy/core/lib/format";
import { signedWon } from "@buildpy/core/modules/change-order/view";

import { cn } from "@/lib/utils";

interface Props {
  // 원 단위 금액 — null·undefined·NaN 은 fallback(0 으로 바꾸지 않는다).
  value: number | null | undefined;
  // total 은 화면의 합계 한 자리(PageHeader 제목 크기).
  size?: "default" | "total";
  // 증감 금액 — 양수 「+」, 음수 「-」, 0 은 부호 없음(change-order 의 signedWon).
  signed?: boolean;
  fallback?: string;
  className?: string;
}

const SIZE = {
  default: "",
  total: "text-2xl font-semibold tracking-tight",
} as const;

// 원 아래는 없다 — 반올림하고 -0 은 0 으로(「-0원」 금지).
const toWon = (value: number): number => Math.round(value) || 0;

/** 금액 한 칸 — `@buildpy/core/lib/format` 의 formatWon(콤마 + 「원」, ₩ 없음) + tabular-nums + 한 줄 · 원 아래는 반올림(-0 은 0) · 컴포넌트에서 toLocaleString·「원」 붙이기를 직접 하지 않는다 · 증감은 signed(+1,200원 · -300원 · 0원, signedWon 과 같은 규칙) · 값이 없으면 0 이 아니라 EMPTY_MARK(—, 스크린리더에는 「금액 없음」) · 표·합계 열에서는 놓이는 칸이 text-right 로 오른쪽 정렬 · 화면의 합계 한 자리는 size="total"(PageHeader 제목 크기). */
const Money = ({ value, size = "default", signed = false, fallback = EMPTY_MARK, className }: Props) => {
  const cls = cn("whitespace-nowrap tabular-nums", SIZE[size], className);
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return (
      <span className={cls}>
        <span aria-hidden="true">{fallback}</span>
        <span className="sr-only">금액 없음</span>
      </span>
    );
  }
  const won = toWon(value);
  return <span className={cls}>{signed ? signedWon(won) : formatWon(won)}</span>;
};

export default Money;
